import React from 'react';
import styled from 'styled-components';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { borders } from '../../design-system/tokens/borders';
import { hexToRgba } from '../../utils/colorUtils';

interface AuthWizardShellProps {
  title: string;
  subtitle?: string;
  backSlot?: React.ReactNode;
  children: React.ReactNode;
  /** Optional sticky footer (e.g. wizard Next/Back actions). */
  footerSlot?: React.ReactNode;
  /** Optional id for the page heading (focus target on step change). */
  titleId?: string;
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 73px);
  min-height: 0;
  max-height: calc(100vh - 73px);
  position: relative;
  background: ${props => props.theme.colors.surface};
  overflow: hidden;

  ${breakpoints.down.md} {
    height: auto;
    max-height: none;
    min-height: calc(100vh - 73px);
    overflow: visible;
  }
`;

const FormPanel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${spacing[6]} ${spacing[5]} ${spacing[8]};
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;

  ${breakpoints.down.md} {
    padding: ${spacing[5]} ${spacing[4]} ${spacing[10]};
    overflow: visible;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
    z-index: 0;
  }
`;

const FormInner = styled.div`
  width: 100%;
  max-width: 960px;
  z-index: 1;
  position: relative;
`;

const PageHeader = styled.header`
  margin-bottom: ${spacing[5]};
`;

const Title = styled(Heading)`
  margin-bottom: ${spacing[2]};
  color: ${props => props.theme.colors.primary};
`;

const Subtitle = styled(Text)`
  display: block;
  margin-bottom: ${spacing[4]};
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: ${spacing[4]} ${spacing[6]};
  padding-bottom: max(${spacing[4]}, env(safe-area-inset-bottom, 0px));
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  width: 100%;
  z-index: 5;
  box-shadow: 0 -4px 20px ${props => hexToRgba(props.theme.colors.shadow || '#000', 0.08)};

  ${breakpoints.down.md} {
    padding: ${spacing[3]} ${spacing[4]};
    padding-bottom: max(${spacing[3]}, env(safe-area-inset-bottom, 0px));
    position: sticky;
    bottom: 0;
  }
`;

const FooterInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[3]};

  ${breakpoints.down.sm} {
    flex-direction: column-reverse;
    align-items: stretch;

    & > * {
      width: 100%;
    }
  }
`;

/**
 * Full-page auth shell for multi-step registration wizards.
 * Single-column form panel with docked footer actions outside the scroll area.
 */
export const AuthWizardShell: React.FC<AuthWizardShellProps> = ({
  title,
  subtitle,
  backSlot,
  children,
  footerSlot,
  titleId = 'auth-wizard-title',
}) => (
  <Shell>
    <FormPanel data-auth-form-panel id="register-form-scroll">
      <FormInner>
        <PageHeader>
          <div id={titleId} tabIndex={-1}>
            <Title variant="h1">{title}</Title>
          </div>
          {subtitle && (
            <Subtitle color="secondary" size="sm">
              {subtitle}
            </Subtitle>
          )}
          {backSlot}
        </PageHeader>

        {children}
      </FormInner>
    </FormPanel>
    {footerSlot && (
      <Footer>
        <FooterInner>{footerSlot}</FooterInner>
      </Footer>
    )}
  </Shell>
);

export default AuthWizardShell;
